import { FC, useCallback, useLayoutEffect, useMemo } from 'react';
import { useRouter, usePathname } from '@/lib/router';
import { useTranslation } from 'react-i18next';
import { Navigation } from '@pcmp/react-coworker-components';
import { useModal } from '@pcmp/react-providers';
import { AvatarProps } from '@ingka/avatar';
import { ROUTES } from '@/lib/constants';
import { initials } from '@/lib/utils/string';
import { useSession, useUser } from '@/redux/features/session/hooks';
import { MaintenanceBanner } from './banner';
import {
  useActiveLocationPermissions,
  useShoppingBag,
} from '@/redux/features/shopping-bag/hooks';
import { getHomePath } from '@/lib/utils/router';
import { useMediaQuery } from 'usehooks-ts';
import { useGetUserProfileImageQuery } from '@/redux/graph/hooks';
import { useMenuContext } from './menu';
import { OrderType } from '@/redux/back-dashboard-api';

const { TopNavItem, NavItemContainer, TopNavigation } = Navigation;

export const Header: FC<{
  fullWidth?: boolean;
}> = ({ fullWidth }) => {
  // Hooks
  const { t } = useTranslation();
  const router = useRouter();
  const route = usePathname();
  const { openModal } = useModal();
  const isMd = useMediaQuery('(min-width: 768px)');
  const { open: openMenu, close: closeMenu } = useMenuContext();
  // Default to Order mode for shopping bag in header
  const orderType = OrderType.Order;
  // Data
  const session = useSession();
  const user = useUser();
  const { data: profileImageSrc } = useGetUserProfileImageQuery(
    { email: user?.email ?? '' },
    { skip: !user?.email }
  );
  const shoppingBag = useShoppingBag();
  const userRoles = useActiveLocationPermissions();
  // Event handlers
  const navigateTo = useCallback(
    (path: string) => () => router.push(path),
    [router]
  );
  const handleShoppingBag = useCallback(async () => {
    const component = await import('@/components/siders/shopping-bag').then(
      (mod) => mod.default
    );

    openModal({
      component,
      props: { orderType },
    });
  }, [openModal, navigateTo, orderType]);
  const handleOpenUserProfile = useCallback(async () => {
    const component = await import('@/components/siders/user-profile').then(
      (mod) => mod.UserProfileSider
    );

    openModal({
      component,
      props: {},
    });
  }, [openModal]);
  const avatar: AvatarProps = useMemo(
    () =>
      user
        ? {
            text: initials(user.name || user.email || '').slice(0, 2),
            screenReaderText: user.name || user.email || t('label.loading'),
            onClick: handleOpenUserProfile,
            imageProps: {
              src: profileImageSrc || '',
            },
          }
        : {
            screenReaderText: t('label.loading'),
            withIcon: true,
          },
    [user, handleOpenUserProfile, t]
  );

  // Close menu on md breakpoint change
  useLayoutEffect(() => {
    closeMenu();
  }, [isMd]);

  return (
    <div data-testid="header" className="sticky top-0 left-0 w-full z-40">
      <MaintenanceBanner />

      <TopNavigation
        onTitleClick={() => router.push(getHomePath())}
        fullWidth={fullWidth}
        title="CHECKA"
        avatar={avatar}
        onShoppingBagClick={handleShoppingBag}
        shoppingBagAmount={
          shoppingBag?.shoppingItems?.reduce(
            (sum, item) => sum + (item.amount || 0),
            0
          ) || undefined
        }
        onMenuClick={isMd ? undefined : openMenu}
      >
        <NavItemContainer className="sm:!hidden md:!flex">
          {ROUTES.map(({ path, translationKey, icon, isActive, isVisible }) => {
            if (
              !isVisible({
                isAdmin: userRoles.isAdmin,
                isInvitation: !!session.invitationToken,
                isPlatformOwner: userRoles.isSuperAdmin,
              })
            )
              return null;

            return (
              <TopNavItem
                key={path}
                text={t(translationKey)}
                icon={icon}
                onClick={navigateTo(path)}
                active={isActive(route || '')}
              />
            );
          })}
        </NavItemContainer>
      </TopNavigation>
    </div>
  );
};
